'use client';

import { useCallback, useMemo, useState, type ChangeEvent } from 'react';

import { type AuthPhonePolicyCountry, getAuthOtpCodeLength, getAuthPhonePolicyCountry, getAuthPhoneProvider } from '@/features/auth/authPhonePolicy';
import { getLocalizedAuthErrorMessage } from '@/features/auth/errorMessages';
import { formatAuthPhoneNumberInput, normalizePhoneNumberForBackend } from '@/features/auth/phone';
import type { AuthAccountRecoveryCodesRequiredResponse, AuthVerifyAuthenticatedResponse } from '@/features/auth/types';
import type { Dictionary } from '@/features/i18n/dictionaries';
import type { SupportedLocale } from '@/features/i18n/logic';
import { getOrCreateBrowserDeviceId } from '@/lib/device/browserDeviceId';

import { AuthApiError, completeChangedNumberRecovery, requestChangedNumberRecoveryOtp } from './api';

type ChangedNumberRecoveryStep = 'details' | 'otp';

type UseChangedNumberRecoveryFlowArgs = {
    countryById: Map<string, AuthPhonePolicyCountry>;
    dictionary: Dictionary['auth'];
    isSubmitting: boolean;
    locale: SupportedLocale;
    onAuthenticated: (response: AuthVerifyAuthenticatedResponse) => void;
    onRecoveryCodesRequired: (response: AuthAccountRecoveryCodesRequiredResponse) => void;
    phone: string;
    selectedCountry: AuthPhonePolicyCountry;
    setErrorCode: (errorCode: string | null) => void;
    setErrorText: (errorText: string) => void;
    setIsSubmitting: (isSubmitting: boolean) => void;
};

const normalizeChangedNumberOtpCode = (value: string, length: number) => value.replace(/[^\d]/g, '').slice(0, length);

export const useChangedNumberRecoveryFlow = ({
    countryById,
    dictionary,
    isSubmitting,
    locale,
    onAuthenticated,
    onRecoveryCodesRequired,
    phone,
    selectedCountry,
    setErrorCode,
    setErrorText,
    setIsSubmitting,
}: UseChangedNumberRecoveryFlowArgs) => {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<ChangedNumberRecoveryStep>('details');
    const [newCountryOptionId, setNewCountryOptionId] = useState(selectedCountry.id);
    const [newPhone, setNewPhone] = useState('');
    const [recoveryCode, setRecoveryCode] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [statusText, setStatusText] = useState('');
    const newCountry = useMemo(
        () =>
            getAuthPhonePolicyCountry({
                countryById,
                countryOptionId: newCountryOptionId,
            }) ?? selectedCountry,
        [countryById, newCountryOptionId, selectedCountry]
    );
    const otpCodeLength = getAuthOtpCodeLength(
        getAuthPhoneProvider({
            countryById,
            countryOptionId: newCountry.id,
        })
    );
    const normalizedOldPhone = useMemo(
        () =>
            normalizePhoneNumberForBackend({
                phoneNumber: phone,
                countryCode: selectedCountry.countryCode,
                countryOptionId: selectedCountry.id,
            }),
        [phone, selectedCountry.countryCode, selectedCountry.id]
    );
    const normalizedNewPhone = useMemo(
        () =>
            normalizePhoneNumberForBackend({
                phoneNumber: newPhone,
                countryCode: newCountry.countryCode,
                countryOptionId: newCountry.id,
            }),
        [newCountry.countryCode, newCountry.id, newPhone]
    );
    const canRequestOtp =
        selectedCountry.enabled && newCountry.enabled && Boolean(normalizedOldPhone) && Boolean(normalizedNewPhone) && recoveryCode.trim().length > 0;
    const canComplete = canRequestOtp && step === 'otp' && otpCode.trim().length === otpCodeLength;

    const clearError = useCallback(() => {
        setErrorText('');
        setErrorCode(null);
    }, [setErrorCode, setErrorText]);

    const reset = useCallback(() => {
        setIsOpen(false);
        setStep('details');
        setNewCountryOptionId((currentCountryOptionId) => (countryById.has(currentCountryOptionId) ? currentCountryOptionId : selectedCountry.id));
        setNewPhone('');
        setRecoveryCode('');
        setOtpCode('');
        setStatusText('');
    }, [countryById, selectedCountry.id]);

    const open = useCallback(() => {
        setIsOpen(true);
        setStatusText('');
        clearError();
    }, [clearError]);

    const close = useCallback(() => {
        if (!isSubmitting) {
            reset();
        }
    }, [isSubmitting, reset]);

    const handleCountryOptionChange = useCallback(
        (event: ChangeEvent<HTMLSelectElement>) => {
            const nextCountryOptionId = event.target.value;
            const nextCountry = getAuthPhonePolicyCountry({
                countryById,
                countryOptionId: nextCountryOptionId,
            });

            setNewCountryOptionId(nextCountryOptionId);
            setOtpCode('');
            setStatusText('');
            setStep('details');
            clearError();
            setNewPhone((currentPhone) =>
                nextCountry
                    ? formatAuthPhoneNumberInput({
                          phoneNumber: currentPhone,
                          countryCode: nextCountry.countryCode,
                          countryOptionId: nextCountry.id,
                      })
                    : ''
            );
        },
        [clearError, countryById]
    );

    const handleNewPhoneChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setNewPhone(
                formatAuthPhoneNumberInput({
                    phoneNumber: event.target.value,
                    countryCode: newCountry.countryCode,
                    countryOptionId: newCountry.id,
                })
            );
            setOtpCode('');
            setStatusText('');
            setStep('details');
            clearError();
        },
        [clearError, newCountry.countryCode, newCountry.id]
    );

    const handleRecoveryCodeChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setRecoveryCode(event.target.value);
            setStatusText('');
            setStep('details');
            clearError();
        },
        [clearError]
    );

    const handleOtpCodeChange = useCallback(
        (nextOtpCode: string) => {
            setOtpCode(normalizeChangedNumberOtpCode(nextOtpCode, otpCodeLength));
            setStatusText('');
            clearError();
        },
        [clearError, otpCodeLength]
    );

    const requestOtp = useCallback(async () => {
        if (!canRequestOtp || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setErrorText('');
        setErrorCode(null);
        setStatusText('');

        try {
            await requestChangedNumberRecoveryOtp({
                oldPhone: normalizedOldPhone,
                oldCountryOptionId: selectedCountry.id,
                newPhone: normalizedNewPhone,
                newCountryOptionId: newCountry.id,
                recoveryCode: recoveryCode.trim(),
                issueStep: 'initial',
            });
            setOtpCode('');
            setStep('otp');
            setStatusText(dictionary.changedNumberRecoveryOtpSent);
        } catch (error) {
            setErrorCode(error instanceof AuthApiError ? error.code : null);
            setErrorText(
                getLocalizedAuthErrorMessage({
                    error,
                    locale,
                    fallback: dictionary.requestOtpFailed,
                    isRegistrationFlow: false,
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [
        canRequestOtp,
        dictionary.changedNumberRecoveryOtpSent,
        dictionary.requestOtpFailed,
        isSubmitting,
        locale,
        newCountry.id,
        normalizedNewPhone,
        normalizedOldPhone,
        recoveryCode,
        selectedCountry.id,
        setErrorCode,
        setErrorText,
        setIsSubmitting,
    ]);

    const complete = useCallback(async () => {
        if (!canComplete || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setErrorText('');
        setErrorCode(null);

        try {
            const response = await completeChangedNumberRecovery({
                oldPhone: normalizedOldPhone,
                oldCountryOptionId: selectedCountry.id,
                newPhone: normalizedNewPhone,
                newCountryOptionId: newCountry.id,
                newPhoneOtpCode: otpCode.trim(),
                recoveryCode: recoveryCode.trim(),
                deviceId: getOrCreateBrowserDeviceId(),
            });

            if (response.status === 'authenticated') {
                onAuthenticated(response);
                return;
            }

            onRecoveryCodesRequired(response);
            reset();
        } catch (error) {
            setErrorCode(error instanceof AuthApiError ? error.code : null);
            setErrorText(
                getLocalizedAuthErrorMessage({
                    error,
                    locale,
                    fallback: dictionary.verifyOtpFailed,
                    isRegistrationFlow: false,
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [
        canComplete,
        dictionary.verifyOtpFailed,
        isSubmitting,
        locale,
        newCountry.id,
        normalizedNewPhone,
        normalizedOldPhone,
        onAuthenticated,
        onRecoveryCodesRequired,
        otpCode,
        recoveryCode,
        reset,
        selectedCountry.id,
        setErrorCode,
        setErrorText,
        setIsSubmitting,
    ]);

    return {
        canCompleteChangedNumberRecovery: canComplete,
        canRequestChangedNumberRecoveryOtp: canRequestOtp,
        changedNumberNewCountryOptionId: newCountryOptionId,
        changedNumberNewPhone: newPhone,
        changedNumberOtpCode: otpCode,
        changedNumberOtpCodeLength: otpCodeLength,
        changedNumberRecoveryCode: recoveryCode,
        changedNumberRecoveryStep: step,
        changedNumberStatusText: statusText,
        handleChangedNumberNewCountryOptionChange: handleCountryOptionChange,
        handleChangedNumberNewPhoneChange: handleNewPhoneChange,
        handleChangedNumberOtpCodeChange: handleOtpCodeChange,
        handleChangedNumberRecoveryCodeChange: handleRecoveryCodeChange,
        handleCloseChangedNumberRecovery: close,
        handleCompleteChangedNumberRecovery: complete,
        handleOpenChangedNumberRecovery: open,
        handleRequestChangedNumberRecoveryOtp: requestOtp,
        isChangedNumberRecoveryOpen: isOpen,
        reset,
    };
};
